import React, { useEffect, useState } from "react";
import { Download, X, Share2 } from "lucide-react";

/**
 * Install-to-home-screen prompt for License to Love.
 * - Android/Chrome/Edge: catches `beforeinstallprompt`, shows native install dialog on tap.
 * - iOS Safari: shows manual instructions ("Share → Add to Home Screen") since iOS has no API.
 * - Auto-hides once installed or dismissed for 30 days.
 */
const DISMISS_KEY = "ltl_install_dismissed_at";
const DISMISS_DAYS = 30;

function isIos() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}
function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function wasRecentlyDismissed() {
  const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!t) return false;
  return Date.now() - t < DISMISS_DAYS * 24 * 3600 * 1000;
}

export function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari: show a soft prompt after 6s of engagement
    if (isIos() && !isStandalone()) {
      const t = setTimeout(() => setVisible(true), 6000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onPrompt);
        clearTimeout(t);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIosSheet(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice.catch(() => null);
      if (choice?.outcome === "accepted") {
        setVisible(false);
      } else {
        dismiss();
      }
      setDeferred(null);
    } else if (isIos()) {
      setShowIosSheet(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Bottom banner */}
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[80]"
        style={{ animation: "reveal 500ms ease-out" }}
      >
        <div
          className="card-paper p-4 flex items-center gap-3 shadow-2xl"
          style={{ border: "2px double #D4A574" }}
        >
          <img src="/icons/icon-192.png" alt="" className="w-12 h-12 rounded-xl shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-ochre-300 font-mono">
              Install the app
            </div>
            <div
              className="font-display text-ink text-lg leading-tight"
              style={{ fontFamily: "Fraunces" }}
            >
              Keep <em className="italic text-ivy">Wholesome</em> close.
            </div>
          </div>
          <button
            data-testid="pwa-install-btn"
            onClick={install}
            className="btn-pill bg-ivy text-parchment hover:bg-ivy-600 text-sm flex items-center gap-1.5 shrink-0"
          >
            <Download size={13} /> Install
          </button>
          <button
            data-testid="pwa-dismiss"
            onClick={dismiss}
            aria-label="Dismiss"
            className="p-1.5 rounded-full text-ink/40 hover:text-ink hover:bg-parchment-50"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* iOS "Add to Home Screen" sheet */}
      {showIosSheet && (
        <div
          data-testid="pwa-ios-sheet"
          className="fixed inset-0 z-[90] flex items-end md:items-center justify-center bg-ink/60 backdrop-blur-sm"
          onClick={() => setShowIosSheet(false)}
        >
          <div
            className="card-paper w-full md:max-w-md m-4 p-6 space-y-4"
            style={{ border: "2px double #D4A574" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div className="eyebrow text-ochre-300">Install on iPhone</div>
              <button
                onClick={() => setShowIosSheet(false)}
                className="text-ink/40 hover:text-ink"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <h3
              className="font-display text-2xl text-ink"
              style={{ fontFamily: "Fraunces" }}
            >
              Add to Home Screen.
            </h3>
            <ol className="space-y-3 text-sm text-ink/75">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-ivy text-parchment inline-flex items-center justify-center text-xs shrink-0">
                  1
                </span>
                <span>
                  Tap the <strong>Share</strong> button
                  <Share2 size={14} className="inline mx-1 text-ivy" />
                  at the bottom of Safari.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-ivy text-parchment inline-flex items-center justify-center text-xs shrink-0">
                  2
                </span>
                <span>
                  Scroll down and tap <strong>Add to Home Screen</strong>.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-ivy text-parchment inline-flex items-center justify-center text-xs shrink-0">
                  3
                </span>
                <span>
                  Tap <strong>Add</strong> — Wholesome will appear as an app on your Home Screen.
                </span>
              </li>
            </ol>
            <button
              data-testid="pwa-ios-close"
              onClick={dismiss}
              className="btn-pill bg-ink text-parchment hover:bg-ivy w-full"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
